import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FeedPage, MetricView } from './types'
import { request } from './client'
import type { AuthorFilters, PortraitFieldKey } from '../lib/authorInsights'

export type AnalysisStatus = 'idle' | 'queued' | 'processing' | 'partial' | 'ready' | 'failed' | 'disabled'
export type AnnotationStatus = 'pending' | 'ready' | 'preliminary' | 'waiting_text' | 'no_text' | 'undefined' | 'failed' | 'stale'
export type ContentRole = 'attract' | 'explain' | 'trust' | 'objection' | 'offer' | 'personal' | 'unknown'
export interface SourceEvidence {
  kind: 'profile' | 'post'
  field: 'biography' | 'caption' | 'text' | 'transcript'
  quote: string
  post_id: number | null
  snapshot_id: number | null
  observed_at: string | null
  url: string | null
  start: number | null
  end: number | null
  start_seconds: number | null
  end_seconds: number | null
  supports: string[]
  context_source_id: number | null
  attribution: 'owner' | 'coauthor' | 'repost' | 'profile' | null
  is_stale: boolean
  stale_reason: 'source_changed' | 'source_unverified' | 'profile_changed' | null
}
export interface SourcePortraitField {
  key: PortraitFieldKey
  value: string | null
  status: 'declared' | 'inferred' | 'confirmed' | 'missing'
  evidence: SourceEvidence[]
  manual: boolean
  edited_by: string | null
  edited_at: string | null
}
export interface SourcePortrait {
  revision: number
  status: AnalysisStatus
  fields: SourcePortraitField[]
  updated_at: string | null
  corpus_days: number
  corpus_posts: number
  analyzed_posts: number
  has_new_suggestion: boolean
  has_stale_evidence: boolean
  auto_fields: SourcePortraitField[]
}
export interface SourceBenchmark {
  kind: string
  metric: string
  definition: string
  provider: string
  value: number | null
  samples: number
  reliable: boolean
  computed_at: string | null
  window_days: number
  maturity_hours: number
}
export interface SourceInsightGroup {
  id: string
  title: string
  description: string | null
  posts: number
  share: number
  evaluated: number
  outliers: number
  outlier_rate: number | null
  median_xn: number | null
  low_data: boolean
  system: boolean
}
export interface SourceInsights {
  source_id: number
  revision: number
  enabled: boolean
  status: AnalysisStatus
  can_generate: boolean
  can_edit: boolean
  error: string | null
  profile_observed_at: string | null
  profile_provider: string | null
  content_until: string | null
  updated_at: string | null
  as_of: string
  period: {
    period_days: number | null
    date_from: string | null
    date_to: string | null
    since: string | null
    until: string
    all_time: boolean
    timezone: string
  }
  format: 'video' | 'text' | null
  group_by: 'topic' | 'rubric' | 'hook' | 'role' | 'cta'
  scope: 'own' | 'reposts'
  metric_view: MetricView
  collection_mode: string | null
  coverage: {
    total_posts: number
    processed_posts: number
    labeled_posts: number
    reliable_posts: number
    pending_posts: number
    waiting_text: number
    no_text: number
    undefined: number
    failed: number
    preliminary: number
    stale: number
    reposts: number
    archive_complete: boolean | null
  }
  portrait: SourcePortrait
  performance: {
    benchmarks: SourceBenchmark[]
    evaluated_posts: number
    outliers: number
    outlier_rate: number | null
    median_xn: number | null
    p25_xn: number | null
    p75_xn: number | null
    young_posts: number
    missing_mature: number
    missing_baseline: number
    best_post_ids: number[]
    below_post_ids: number[]
    comparable: boolean
    observed_since: string | null
    observed_until: string | null
  }
  groups: SourceInsightGroup[]
  progress: {
    queued: number
    processing: number
    failed: number
    completed: number
    waiting_reason: string | null
    daily_calls: number
    daily_limit: number
  }
}
export interface SourceTopic { id: string; title: string; description: string | null }
export interface AnnotationValues {
  topic_id: string | null
  secondary_topic_ids: string[]
  rubric: string | null
  hook: string | null
  role: ContentRole
  cta: string | null
  case: string | null
  objection: string | null
}
export type AnnotationKey = keyof AnnotationValues
export interface SourceAnnotation {
  source_id: number
  post_id: number
  revision: number
  status: AnnotationStatus
  values: AnnotationValues
  auto_values: AnnotationValues
  manual_fields: string[]
  evidence: SourceEvidence[]
  topics: SourceTopic[]
  updated_at: string | null
  input_hash: string | null
  preliminary: boolean
}
export interface InsightAction { status: 'queued'; queued: number; revision: number }
export interface SourceInsightPostsFilters extends AuthorFilters {
  group_id?: string
  selection?: 'all' | 'best' | 'below'
  sort?: string
  only_outliers?: boolean
  limit?: number
}
export interface PortraitPatch { expected_revision: number; fields: Partial<Record<PortraitFieldKey, string | null>> }
export interface AnnotationPatch { expected_revision: number; fields: Partial<Record<AnnotationKey, string | string[] | null>> }

export function insightQuery(values: object): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(values)) if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  return params.toString()
}
const base = (sourceId: number) => `/api/sources/${sourceId}/insights`
export const authorInsightKeys = {
  source: (sourceId: number) => ['author-insights', sourceId] as const,
  view: (sourceId: number, filters: AuthorFilters) => ['author-insights', sourceId, 'view', filters] as const,
  posts: (sourceId: number, filters: SourceInsightPostsFilters) => ['author-insights', sourceId, 'posts', filters] as const,
  annotation: (sourceId: number, postId: number, metricView?: MetricView) => ['author-insights', sourceId, 'annotation', postId, metricView] as const,
  topics: (sourceId: number) => ['author-insights', sourceId, 'topics'] as const,
}
export const authorInsightApi = {
  get: (sourceId: number, filters: AuthorFilters, signal?: AbortSignal) => request<SourceInsights>(`${base(sourceId)}?${insightQuery(filters)}`, { signal }),
  posts: (sourceId: number, filters: SourceInsightPostsFilters & { cursor?: string }, signal?: AbortSignal) => request<FeedPage>(`${base(sourceId)}/posts?${insightQuery(filters)}`, { signal }),
  annotation: (sourceId: number, postId: number, signal?: AbortSignal, metric_view?: MetricView) => request<SourceAnnotation>(`${base(sourceId)}/posts/${postId}/annotation?${insightQuery({ metric_view })}`, { signal }),
  topics: (sourceId: number, signal?: AbortSignal) => request<SourceTopic[]>(`${base(sourceId)}/topics`, { signal }),
  start: (sourceId: number, metric_view?: MetricView) => request<InsightAction>(`${base(sourceId)}/start?${insightQuery({ metric_view })}`, { method: 'POST' }),
  retry: (sourceId: number, metric_view?: MetricView) => request<InsightAction>(`${base(sourceId)}/retry?${insightQuery({ metric_view })}`, { method: 'POST' }),
  portrait: (sourceId: number, body: PortraitPatch, metric_view?: MetricView) => request<SourcePortrait>(`${base(sourceId)}/portrait?${insightQuery({ metric_view })}`, { method: 'PATCH', body }),
  patchAnnotation: (sourceId: number, postId: number, body: AnnotationPatch, metric_view?: MetricView) => request<SourceAnnotation>(`${base(sourceId)}/posts/${postId}/annotation?${insightQuery({ metric_view })}`, { method: 'PATCH', body }),
  renameTopic: (sourceId: number, topicId: string, expected_revision: number, title: string) => request<SourceTopic>(`${base(sourceId)}/topics/${encodeURIComponent(topicId)}`, { method: 'PATCH', body: { expected_revision, title } }),
  mergeTopic: (sourceId: number, topicId: string, expected_revision: number, into_topic_id: string) => request<SourceTopic[]>(`${base(sourceId)}/topics/${encodeURIComponent(topicId)}/merge`, { method: 'POST', body: { expected_revision, into_topic_id } }),
}

export function useAuthorInsights(sourceId: number, filters: AuthorFilters, active = true) {
  return useQuery({
    queryKey: authorInsightKeys.view(sourceId, filters),
    queryFn: ({ signal }) => authorInsightApi.get(sourceId, filters, signal),
    enabled: sourceId > 0,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => active && (query.state.data?.progress.queued || query.state.data?.progress.processing) ? 5000 : false,
  })
}
export function useAuthorPosts(sourceId: number, filters: SourceInsightPostsFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: authorInsightKeys.posts(sourceId, filters),
    queryFn: ({ pageParam, signal }) => authorInsightApi.posts(sourceId, { ...filters, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: enabled && sourceId > 0,
    refetchOnWindowFocus: false,
  })
}
export function useSourceAnnotation(sourceId: number, postId: number | null, enabled = true, metricView?: MetricView) {
  return useQuery({ queryKey: authorInsightKeys.annotation(sourceId, postId ?? 0, metricView), queryFn: ({ signal }) => authorInsightApi.annotation(sourceId, postId!, signal, metricView), enabled: enabled && sourceId > 0 && postId !== null, refetchOnWindowFocus: false })
}
export function useSourceTopics(sourceId: number, enabled = true) {
  return useQuery({ queryKey: authorInsightKeys.topics(sourceId), queryFn: ({ signal }) => authorInsightApi.topics(sourceId, signal), enabled: enabled && sourceId > 0, refetchOnWindowFocus: false })
}
function useInsightMutation<T, R>(sourceId: number, mutate: (args: T) => Promise<R>) {
  const client = useQueryClient()
  return useMutation({ mutationFn: mutate, onSuccess: () => client.invalidateQueries({ queryKey: authorInsightKeys.source(sourceId) }) })
}
export function useStartAuthorInsights(sourceId: number, metricView?: MetricView) { return useInsightMutation(sourceId, (retry: boolean) => retry ? authorInsightApi.retry(sourceId, metricView) : authorInsightApi.start(sourceId, metricView)) }
export function useSaveAuthorPortrait(sourceId: number, metricView?: MetricView) { return useInsightMutation(sourceId, (body: PortraitPatch) => authorInsightApi.portrait(sourceId, body, metricView)) }
export function useSaveSourceAnnotation(sourceId: number, postId: number, metricView?: MetricView) { return useInsightMutation(sourceId, (body: AnnotationPatch) => authorInsightApi.patchAnnotation(sourceId, postId, body, metricView)) }
export function useRenameSourceTopic(sourceId: number) { return useInsightMutation(sourceId, (value: { id: string; revision: number; title: string }) => authorInsightApi.renameTopic(sourceId, value.id, value.revision, value.title)) }
export function useMergeSourceTopic(sourceId: number) { return useInsightMutation(sourceId, (value: { id: string; revision: number; into: string }) => authorInsightApi.mergeTopic(sourceId, value.id, value.revision, value.into)) }
